import { z, ZodObject, ZodType } from 'zod';

type NullableShape<T extends Record<string, ZodType>> = {
  [K in keyof T]: z.ZodNullable<T[K]>;
};

/**
 * Makes all properties of a Zod object schema nullable (shallow).
 * Does not recurse into nested objects.
 *
 * @example
 * const schema = z.object({ name: z.string(), age: z.number() });
 * const nullableSchema = makeShallowNullable(schema);
 * // Result: z.object({ name: z.string().nullable(), age: z.number().nullable() })
 */
export function makeSchemaFieldsNullable<T extends Record<string, ZodType>>(
  schema: ZodObject<T>
): z.ZodObject<NullableShape<T>> {
  const shape = schema.shape as Record<string, ZodType>;
  const nullableShape = {} as NullableShape<T>;

  for (const key in shape) {
    nullableShape[key as keyof T] = shape[key].nullable() as NullableShape<T>[keyof T];
  }

  return z.object(nullableShape);
}
